import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatTL } from "@/lib/api";
import { toast } from "sonner";
import { Layers, List, User, Hash, ArrowRight, Zap, Shield, Lock, ChevronDown, Search, Tag, Check, Loader2 } from "lucide-react";

function SearchSelect({ items, value, onChange, placeholder, disabled, testid, getLabel, getKey }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = useMemo(() => {
    if (!q) return items;
    return items.filter((it) => getLabel(it).toLowerCase().includes(q.toLowerCase()));
  }, [items, q, getLabel]);
  const selected = items.find((it) => getKey(it) === value);
  return (
    <div className="relative" ref={ref}>
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)} data-testid={testid}
        className={`w-full flex items-center justify-between gap-2 bg-[#171922] border border-white/10 rounded-xl px-4 py-3.5 text-left transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-white/20 focus:border-[#d8b4fe]"}`}>
        <span className={`text-sm truncate ${selected ? "text-white" : "text-gray-500"}`} title={selected ? getLabel(selected) : ""}>
          {selected ? getLabel(selected) : placeholder}
        </span>
        <ChevronDown size={18} className={`text-gray-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && !disabled && (
        <div className="absolute z-30 mt-2 w-full card-surface p-2 max-h-72 overflow-auto shadow-2xl" data-testid={`${testid}-dropdown`}>
          <div className="flex items-center gap-2 bg-[#171922] rounded-lg px-3 py-2 mb-2 sticky top-0">
            <Search size={15} className="text-gray-500" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara..."
              className="bg-transparent outline-none text-sm text-white w-full" data-testid={`${testid}-search`} />
          </div>
          {filtered.length === 0 && <div className="text-sm text-gray-500 px-3 py-4 text-center">Sonuç yok</div>}
          {filtered.slice(0, 200).map((it) => (
            <button type="button" key={getKey(it)} onClick={() => { onChange(getKey(it)); setOpen(false); setQ(""); }}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors flex items-start gap-2 ${getKey(it) === value ? "bg-white/10 text-white" : "text-gray-300 hover:bg-white/5"}`}
              data-testid={`${testid}-option`}>
              {getKey(it) === value && <Check size={15} className="text-[#d8b4fe] mt-0.5 shrink-0" />}
              <span className="leading-snug break-words">{getLabel(it)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const STEPS = [
  { n: 1, label: "Hizmet Seçimi" },
  { n: 2, label: "Bilgiler" },
  { n: 3, label: "Ödeme" },
];

export function OrderForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [platforms, setPlatforms] = useState([]);
  const [platform, setPlatform] = useState("");
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [link, setLink] = useState("");
  const [qty, setQty] = useState("");
  const [form, setForm] = useState({ full_name: "", email: "", coupon_code: "" });
  const [quote, setQuote] = useState(null);
  const [loadingSvc, setLoadingSvc] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [couponMsg, setCouponMsg] = useState(null);

  useEffect(() => { api.get("/platforms").then((r) => setPlatforms(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!platform) { setServices([]); setServiceId(""); return; }
    setLoadingSvc(true); setServiceId("");
    api.get(`/services?platform=${platform}`).then((r) => setServices(r.data)).catch(() => {}).finally(() => setLoadingSvc(false));
  }, [platform]);

  const service = services.find((s) => s.service_id === serviceId);
  const clientSubtotal = service && qty ? (service.price_per_1000 * Number(qty)) / 1000 : 0;

  const refreshQuote = async (coupon) => {
    if (!serviceId || !qty) return null;
    try {
      const { data } = await api.post("/orders/quote", { service_id: serviceId, quantity: Number(qty), coupon_code: coupon ?? form.coupon_code });
      setQuote(data);
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return null;
    }
  };

  const goStep2 = async () => {
    if (!platform) return toast.error("Lütfen platform seçin");
    if (!serviceId) return toast.error("Lütfen servis seçin");
    if (!link.trim()) return toast.error("Kullanıcı adı veya link girin");
    if (!qty || Number(qty) <= 0) return toast.error("Miktar girin");
    if (service && (Number(qty) < service.min || Number(qty) > service.max))
      return toast.error(`Miktar ${service.min} - ${service.max} arasında olmalı`);
    const q = await refreshQuote();
    if (q) setStep(2);
  };

  const applyCoupon = async () => {
    if (!form.coupon_code) return;
    const q = await refreshQuote(form.coupon_code);
    if (q) setCouponMsg(q.coupon_valid ? { ok: true, text: `Kupon uygulandı: -${formatTL(q.discount)}` } : { ok: false, text: "Kupon geçersiz" });
  };

  const goStep3 = async () => {
    if (!form.full_name.trim()) return toast.error("Ad soyad girin");
    if (!form.email.trim()) return toast.error("E-posta girin");
    await refreshQuote();
    setStep(3);
  };

  const placeOrder = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        service_id: serviceId, quantity: Number(qty), link,
        full_name: form.full_name, email: form.email, coupon_code: form.coupon_code || null,
      });
      if (data.payment_url) {
        toast.success("Ödeme sayfasına yönlendiriliyorsunuz...");
        window.location.href = data.payment_url;
      } else {
        toast.warning("Sipariş oluşturuldu. Ödeme bağlantısı için sipariş numaranızı takip edin: " + data.order_number);
        navigate(`/siparis-takip?order=${data.order_number}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setPlacing(false); }
  };

  const total = quote ? quote.total : clientSubtotal;

  return (
    <div className="card-surface p-5 sm:p-8" data-testid="order-form">
      <div className="flex items-center justify-between mb-8">
        {STEPS.map((s, i) => (
          <div key={s.n} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-lg transition-all ${step >= s.n ? "bg-white text-[#1f222d]" : "border border-white/20 text-gray-500"}`}>
                {s.n}
              </div>
              <span className={`text-xs mt-2 font-medium ${step >= s.n ? "text-white" : "text-gray-500"}`}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-px flex-1 mx-2 ${step > s.n ? "bg-white/40" : "bg-white/10"}`} />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 bg-[#171922] border border-white/10 rounded-xl px-2">
            <Layers size={18} className="text-gray-400 ml-2" />
            <div className="flex-1 py-1">
              <SearchSelect items={platforms} value={platform} onChange={setPlatform} placeholder="Platform seçiniz.."
                testid="order-platform-select" getLabel={(p) => `${p.label} (${p.count})`} getKey={(p) => p.key} />
            </div>
          </div>
          <div className="flex items-center gap-3 bg-[#171922] border border-white/10 rounded-xl px-2">
            <List size={18} className="text-gray-400 ml-2" />
            <div className="flex-1 py-1">
              <SearchSelect items={services} value={serviceId} onChange={setServiceId}
                placeholder={loadingSvc ? "Yükleniyor..." : platform ? "Servis seçiniz.." : "Önce platform seçin"}
                disabled={!platform || loadingSvc} testid="order-service-select"
                getLabel={(s) => s.name} getKey={(s) => s.service_id} />
            </div>
          </div>
          {service && (
            <div className="text-xs text-gray-400 bg-[#171922] border border-white/10 rounded-xl px-4 py-3 flex flex-wrap gap-x-4 gap-y-1" data-testid="service-info">
              <span>Min: <b className="text-white">{service.min}</b></span>
              <span>Max: <b className="text-white">{service.max}</b></span>
              <span>1000 adet: <b className="text-white">{formatTL(service.price_per_1000)}</b></span>
            </div>
          )}
          <div className="flex items-center gap-3 bg-[#171922] border border-white/10 rounded-xl px-4">
            <User size={18} className="text-gray-400" />
            <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Kullanıcı adı veya profil linki"
              className="flex-1 bg-transparent py-3.5 text-white text-sm outline-none placeholder:text-gray-500" data-testid="order-link-input" />
          </div>
          <div className="flex items-center gap-3 bg-[#171922] border border-white/10 rounded-xl px-4">
            <Hash size={18} className="text-gray-400" />
            <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Miktar"
              className="flex-1 bg-transparent py-3.5 text-white text-sm outline-none placeholder:text-gray-500" data-testid="order-quantity-input" />
          </div>
          <div className="card-surface flex items-center justify-between px-5 py-4">
            <span className="text-gray-400">Toplam Tutar</span>
            <span className="text-3xl font-extrabold text-white" data-testid="order-total">{formatTL(total)}</span>
          </div>
          <button onClick={goStep2} className="btn-gradient w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 hover:scale-[1.01] transition-transform" data-testid="order-continue-btn">
            Devam Et <ArrowRight size={20} />
          </button>
          <div className="flex justify-around text-xs text-gray-400 pt-2">
            <span className="flex items-center gap-1.5"><Zap size={14} /> Anında İşlem</span>
            <span className="flex items-center gap-1.5"><Shield size={14} /> Güvenli Ödeme</span>
            <span className="flex items-center gap-1.5"><Lock size={14} /> SSL Korumalı</span>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Ad Soyad"
            className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm outline-none focus:border-[#d8b4fe]" data-testid="order-name-input" />
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-posta adresiniz"
            className="w-full bg-[#171922] border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm outline-none focus:border-[#d8b4fe]" data-testid="order-email-input" />
          <div className="flex gap-2">
            <div className="flex items-center gap-2 flex-1 bg-[#171922] border border-white/10 rounded-xl px-4">
              <Tag size={16} className="text-gray-400" />
              <input value={form.coupon_code} onChange={(e) => setForm({ ...form, coupon_code: e.target.value })} placeholder="Kupon Kodunuz"
                className="flex-1 bg-transparent py-3.5 text-white text-sm outline-none placeholder:text-gray-500" data-testid="order-coupon-input" />
            </div>
            <button onClick={applyCoupon} className="px-5 rounded-xl bg-white/10 text-white font-semibold text-sm hover:bg-white/15" data-testid="apply-coupon-btn">Uygula</button>
          </div>
          {couponMsg && <p className={`text-sm ${couponMsg.ok ? "text-green-400" : "text-red-400"}`} data-testid="coupon-msg">{couponMsg.text}</p>}
          <div className="flex gap-3 pt-2">
            <button onClick={() => setStep(1)} className="flex-1 py-4 rounded-xl bg-white/5 border border-white/10 text-white font-semibold hover:bg-white/10" data-testid="order-back-btn">Geri</button>
            <button onClick={goStep3} className="btn-gradient flex-1 py-4 rounded-xl font-bold flex items-center justify-center gap-2" data-testid="order-to-summary-btn">Devam Et <ArrowRight size={18} /></button>
          </div>
        </div>
      )}

      {step === 3 && quote && (
        <div className="space-y-3" data-testid="order-summary">
          <h3 className="text-lg font-bold text-white mb-2">Sipariş Özeti</h3>
          {[
            ["Platform", quote.platform_label],
            ["Servis", quote.service_name],
            ["Hedef", link],
            ["Miktar", qty],
            ["Birim (1000)", formatTL(quote.unit_per_1000)],
            ["Ara Toplam", formatTL(quote.subtotal)],
            ...(quote.discount > 0 ? [["Kupon İndirimi", "-" + formatTL(quote.discount)]] : []),
            ["Ad Soyad", form.full_name],
            ["E-posta", form.email],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 text-sm border-b border-white/5 pb-2">
              <span className="text-gray-400 shrink-0">{k}</span>
              <span className="text-white text-right break-all">{v}</span>
            </div>
          ))}
          <div className="flex justify-between items-center pt-2">
            <span className="text-gray-300 font-semibold">Toplam</span>
            <span className="text-2xl font-extrabold text-white" data-testid="summary-total">{formatTL(quote.total)}</span>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setStep(2)} className="flex-1 py-4 rounded-xl bg-white/5 border border-white/10 text-white font-semibold hover:bg-white/10" data-testid="summary-back-btn">Geri</button>
            <button onClick={placeOrder} disabled={placing} className="btn-gradient flex-1 py-4 rounded-xl font-bold flex items-center justify-center gap-2" data-testid="pay-btn">
              {placing ? <><Loader2 size={18} className="animate-spin" /> İşleniyor</> : <><Lock size={16} /> Shopier ile Öde</>}
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center pt-1">Ödeme Shopier güvenli altyapısı ile alınır. Ödeme onaylandığında siparişiniz otomatik işleme alınır.</p>
        </div>
      )}
    </div>
  );
}
